import {InfiniteData} from '@tanstack/query-core';
import {
  infiniteQueryOptions,
  queryOptions,
  UseInfiniteQueryOptions,
  UseQueryOptions,
} from '@tanstack/react-query';
import {postInvenGetNextItem} from '../api/inven/postInvenGetNextItem';
import {
  ALLOW_LEGACY_SCHEME_LIST,
  ALLOW_SCHEME_LIST,
  TICKET_QUERY_KEY_MAP,
} from '../api/ticket/constant';
import {getTicketFreeCheck} from '../api/ticket/getTicketFreeCheck';
import {getTicketHistoryBuy} from '../api/ticket/getTicketHistoryBuy';
import {getTicketHistoryBuyDetail} from '../api/ticket/getTicketHistoryBuyDetail';
import {getTicketList} from '../api/ticket/getTicketList';
import {getTicketMy} from '../api/ticket/getTicketMy';
import {getTicketReadyToUse} from '../api/ticket/getTicketReadyToUse';
import {getTicketSetReadyToUse} from '../api/ticket/getTicketSetReadyToUse';
import type {FetcherOptions} from '../axios/axiosBffFetch';
import {ReadAccessTypeVo} from '../common/shared/api/nicolas/vo/enums/ReadAccessTypeVo.enum';
import {PagewebError} from '../errors/PagewebError.class';
import {createSimpleApiResponseError} from '../graphql/api/createSimpleApiResponseError';
import {createBuyHistory} from '../graphql/factory/BuyHistory';
import {createContentCheckFreeTicket} from '../graphql/factory/ContentCheckFreeTicket';
import {createContentMyTicket} from '../graphql/factory/ContentMyTicket';
import {createContentTicketInfo} from '../graphql/factory/ContentTicketInfo';
import {createLegacySingle} from '../graphql/factory/LegacySingle';
import {createReadyToUseTicket} from '../graphql/factory/ReadyToUseTicket';
import {createSetReadyToUseTicket} from '../graphql/factory/SetReadyToUseTicket';
import {createSimpleImageBanner} from '../graphql/factory/SimpleImageBanner';
import {createTicketHistoryBuyDetail} from '../graphql/factory/TicketHistoryBuyDetail';
import {
  ContentBuyTicketPageQuery,
  ContentBuyTicketPageQueryVariables,
  ContentCheckFreeTicketQuery,
  ContentCheckFreeTicketQueryVariables,
  ContentMyTicketQuery,
  ContentMyTicketQueryVariables,
  ContentTicketHistoryBuyDetailQuery,
  ContentTicketHistoryBuyDetailQueryVariables,
  ContentTicketHistoryBuyQuery,
  ContentTicketHistoryBuyQueryVariables,
  LegacySingle,
  ReadyToUseTicketQuery,
  ReadyToUseTicketQueryVariables,
  SetReadyToUseTicketQuery,
  SetReadyToUseTicketQueryVariables,
} from '../graphql/generated/types';

export const ticketFactory = {
  /**
   * 이용권 구매 페이지 정보 조회
   *
   * ⚠️ Resolver 로직:
   * - includeBanner: true로 고정
   * - allowBannerSchemeList로 banner 필터링
   * - 필터링된 banner_list만 반환
   */
  contentBuyTicketPage: (
    variables: ContentBuyTicketPageQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ContentBuyTicketPageQuery,
        PagewebError,
        ContentBuyTicketPageQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: TICKET_QUERY_KEY_MAP.contentBuyTicketPage(variables),
      queryFn: async () => {
        const response = await getTicketList(
          {
            seriesId: variables.seriesId,
            includeBanner: true,
          },
          fetcherOptions
        );

        const data = response.result;

        const allowBannerSchemeList = [
          ...ALLOW_LEGACY_SCHEME_LIST,
          ...ALLOW_SCHEME_LIST,
        ];

        return {
          contentBuyTicketPage: {
            ticketInfo: data ? createContentTicketInfo(data) : undefined,
            bannerList:
              data?.banner_list
                ?.filter(
                  banner =>
                    banner.scheme &&
                    allowBannerSchemeList.some(scheme =>
                      banner.scheme.startsWith(scheme)
                    )
                )
                .map(createSimpleImageBanner) || [],
          },
        };
      },
      ...options,
    }),

  /**
   * 무료 이용권 확인
   *
   * ⚠️ Resolver 로직:
   * - response 없으면 에러 throw
   * - Factory 함수로 응답 변환
   */
  contentCheckFreeTicket: (
    variables: ContentCheckFreeTicketQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ContentCheckFreeTicketQuery,
        PagewebError,
        ContentCheckFreeTicketQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: TICKET_QUERY_KEY_MAP.contentCheckFreeTicket(variables),
      queryFn: async () => {
        const response = await getTicketFreeCheck(
          {
            seriesId: variables.seriesId,
          },
          fetcherOptions
        );

        if (!response) {
          throw createSimpleApiResponseError(
            '무료 이용권 데이터를 찾을 수 없습니다'
          );
        }

        const data = response.result;

        return {
          contentCheckFreeTicket: createContentCheckFreeTicket(data),
        };
      },
      ...options,
    }),

  /**
   * 보유 이용권 조회
   *
   * ⚠️ Resolver 로직:
   * - response.result 없으면 에러 throw
   * - Factory 함수로 응답 변환
   */
  contentMyTicket: (
    variables: ContentMyTicketQueryVariables,
    options?: Omit<
      UseQueryOptions<ContentMyTicketQuery, PagewebError, ContentMyTicketQuery>,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: TICKET_QUERY_KEY_MAP.contentMyTicket(variables),
      queryFn: async () => {
        const response = await getTicketMy(
          {
            seriesId: variables.seriesId,
            includeSingle: variables.includeSingle ?? undefined,
            onlyPaidTicket: variables.onlyPaidTicket ?? undefined,
            includeWaitfree: variables.includeWaitfree ?? undefined,
          },
          fetcherOptions
        );

        const data = response.result;

        if (!data) {
          throw createSimpleApiResponseError('이용권 보유 정보 가져오기 실패');
        }

        return {
          contentMyTicket: createContentMyTicket(data),
        };
      },
      ...options,
    }),

  /**
   * 이용권 구매 내역 조회 (Infinite)
   *
   * ⚠️ Resolver 로직:
   * - response.result?.list를 map으로 createBuyHistory factory 적용
   * - isEnd, totalCount 반환
   * - ContentTicketHistoryBuyQuery 타입으로 변환 (각 페이지마다 적용)
   *
   * ⚠️ Adapter에서 initialPageParam과 getNextPageParam을 반드시 전달해야 함
   * ⚠️ pageParam은 {page?: number} 객체 타입
   */
  infiniteContentTicketHistoryBuy: (
    variables: ContentTicketHistoryBuyQueryVariables,
    options: Omit<
      UseInfiniteQueryOptions<
        ContentTicketHistoryBuyQuery, // TQueryFnData - GraphQL Query 타입
        PagewebError,
        InfiniteData<
          ContentTicketHistoryBuyQuery,
          ContentTicketHistoryBuyQueryVariables
        >, // TData
        ContentTicketHistoryBuyQuery, // TResult
        ReturnType<typeof TICKET_QUERY_KEY_MAP.infiniteContentTicketHistoryBuy>,
        Partial<ContentTicketHistoryBuyQueryVariables> // TPageParam - 객체 타입
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    infiniteQueryOptions({
      queryKey: TICKET_QUERY_KEY_MAP.infiniteContentTicketHistoryBuy(variables),
      queryFn: async ({pageParam}) => {
        // pageParam이 {page?: number} 형태로 들어옴
        const page = pageParam?.page ?? 0;

        const response = await getTicketHistoryBuy(
          {
            seriesId: variables.seriesId,
            page,
          },
          fetcherOptions
        );

        const data = response.result;

        // ⚠️ Resolver와 동일하게 factory 적용, GraphQL 응답 형태로 래핑
        return {
          contentTicketHistoryBuy: {
            list: data?.list?.map(item => createBuyHistory(item)),
            isEnd: data?.is_end,
            totalCount: data?.total_count,
          },
        };
      },
      ...options,
    }),

  /**
   * 이용권 구매 내역 상세 조회
   *
   * ⚠️ Resolver 로직:
   * - response.result 없으면 에러 throw
   * - Factory 함수로 응답 변환
   */
  contentTicketHistoryBuyDetail: (
    variables: ContentTicketHistoryBuyDetailQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ContentTicketHistoryBuyDetailQuery,
        PagewebError,
        ContentTicketHistoryBuyDetailQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: TICKET_QUERY_KEY_MAP.contentTicketHistoryBuyDetail(variables),
      queryFn: async () => {
        const response = await getTicketHistoryBuyDetail(
          variables,
          fetcherOptions
        );
        const data = response.result;
        if (!data) {
          throw createSimpleApiResponseError(
            '콘텐츠 구매 내역 상세 가져오기 실패'
          );
        }

        return {
          contentTicketHistoryBuyDetail: createTicketHistoryBuyDetail(data),
        };
      },
      ...options,
    }),

  /**
   * 이용권 준비 상태 조회
   *
   * ⚠️ Resolver 로직 (완전 복제됨):
   * - getTicketReadyToUse 호출 (includeSeries: true)
   * - read_access_type이 Expired면 getNextItem 호출
   * - nextItem이 있으면 createLegacySingle 적용
   * - 결과를 {result, nextItem} 형태로 반환
   *
   * ⚠️ 주의:
   * - Query Factory에서 createLegacySingle 적용
   * - Adapter에서는 createReadyToUseTicket만 적용
   */
  readyToUseTicket: (
    variables: ReadyToUseTicketQueryVariables,
    options?: Omit<
      UseQueryOptions<
        ReadyToUseTicketQuery,
        PagewebError,
        ReadyToUseTicketQuery
      >,
      'queryKey' | 'queryFn'
    >
  ) =>
    queryOptions({
      queryKey: TICKET_QUERY_KEY_MAP.readyToUseTicket(variables),
      queryFn: async () => {
        // 1. 첫 번째 API: 이용권 준비 상태 조회
        const {result} = await getTicketReadyToUse({
          productId: variables.productId,
          includeSeries: true,
        });

        // 2. 조건부 API: read_access_type이 Expired면 다음 회차 조회
        let nextItem: LegacySingle | undefined;

        if (result.single.read_access_type === ReadAccessTypeVo.Expired) {
          const nextItemResponse = await postInvenGetNextItem(variables);

          // nextItem이 있으면 createLegacySingle 적용
          if (nextItemResponse.item) {
            nextItem = createLegacySingle(nextItemResponse.item);
          }
        }

        return {
          readyToUseTicket: createReadyToUseTicket({result, nextItem}),
        };
      },
      ...options,
    }),

  /**
   * 세트 이용권 준비 상태 조회
   */
  setReadyToUseTicket: (
    variables: SetReadyToUseTicketQueryVariables,
    options?: Omit<
      UseQueryOptions<
        SetReadyToUseTicketQuery,
        PagewebError,
        SetReadyToUseTicketQuery
      >,
      'queryKey' | 'queryFn'
    >,
    fetcherOptions?: FetcherOptions
  ) =>
    queryOptions({
      queryKey: TICKET_QUERY_KEY_MAP.setReadyToUseTicket(variables),
      queryFn: async () => {
        const response = await getTicketSetReadyToUse(
          variables,
          fetcherOptions
        );

        const data = response.result;

        return {
          setReadyToUseTicket: createSetReadyToUseTicket({result: data}),
        };
      },
      ...options,
    }),
} as const;
